
import {
  BButton,
  BCollapse,
  BContainer,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormTextarea,
  BNavbar,
  BNavbarBrand,
  BNavbarNav,
  BNavbarToggle,
  BNavItem,
  BRow,
  BTab,
  BTabs
} from "bootstrap-vue-3";

import {
  CreditMode,
  EmbedQueryStringBuilder,
  PlanetaryBodies
} from "@wwtelescope/embed-common";
import { defineComponent } from "@vue/runtime-core";
import useClipboard from "vue-clipboard3";

// From Tom Gruner @ http://stackoverflow.com/a/12034334/1660815 (without forward-slash subst)
const entityMap: { [index: string]: string } = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;"
};

function escapeHtml(source: string) {
  return source.replace(/[&<>"']/g, s => entityMap[s]);
}

export default defineComponent({

  components: {
    BButton,
    BCollapse,
    BContainer,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormTextarea,
    BNavbar,
    BNavbarBrand,
    BNavbarNav,
    BNavbarToggle,
    BNavItem,
    BRow,
    BTab,
    BTabs
  },

  data() {
    return {
      CreditMode: CreditMode,
      PlanetaryBodies: PlanetaryBodies,
      qsb: new EmbedQueryStringBuilder(),
      currentTabIndex: 0,
      clipboardNoticeFadeOut: false,
      clipboardNoticeText: "",
      showImageUrlValidity: null as boolean | null,
      suggestDefaultStyling: true
    }
  },

  computed: {
    queryString(): string {
      const qs = new URLSearchParams(this.qsb.toQueryItems()).toString();
      if (qs.length)
        return "?" + qs;
      return "";
    },

    iframeBaseUrl(): string {
      // note: in production, the environment variable will be replaced with a literal value
      if (process.env.NODE_ENV == "development") {
        return "http://localhost:23000/";
      }

      return "https://web.wwtassets.org/embed/1/wwt/";
    },

    iframeSource(): string {
      return `${this.iframeBaseUrl}${this.queryString}`;
    },

    embedCode(): string {
      const style = this.suggestDefaultStyling ? " frameborder=\"0\" style=\"width: 100%; height: 360px;\"" : "";
      return `<iframe class="wwt-embed" src="${escapeHtml(this.iframeSource)}" allow="accelerometer; autoplay; clipboard-write; gyroscope" allowfullscreen ${style}>
      <p>Cannot display WorldWide Telescope because your browser does not support iframes.</p>
      </iframe>`;
    }

  },

  methods: {
    async copyToClipboard(): Promise<void> {
      const { toClipboard } = useClipboard();
      try {
        await toClipboard(this.embedCode);
        this.onClipboardSuccess();
      } catch (e) {
        this.onClipboardError();
      }
    },

    onShowImageUrlInput(url: string) {
      let urlIsOk = false;

      try {
        const parsed = new URL(url);
        const queryParams = new URLSearchParams(parsed.search);

        if (parsed.pathname.toLowerCase() == "/wwtweb/showimage.aspx") {
          const name = (queryParams.get("name") || "").replace(",", "");
          queryParams.set("wtml", "true");
          parsed.search = "?" + queryParams.toString();
          this.qsb.s.wtmlUrl = parsed.toString();
          this.qsb.s.wtmlPlace = name;
          urlIsOk = true;
        }
      } catch {
        // We get an exception if `url` can't be parsed in the `new URL()` call.
        urlIsOk = false;
      }

      this.showImageUrlValidity = urlIsOk;

      if (urlIsOk) {
        // Clear out state for other modes with which we're mutually exclusive.
        this.qsb.planetaryBody = null;
      }
    },

    onClipboardSuccess() {
      this.clipboardNoticeText = "Copied!";
      this.clipboardNoticeFadeOut = true;
      setTimeout(() => {
        this.clipboardNoticeText = "";
        this.clipboardNoticeFadeOut = false;
      }, 2000);
    },

    onClipboardError() {
      this.clipboardNoticeText = "Error copying to clipboard :-(";
      this.clipboardNoticeFadeOut = true;
      setTimeout(() => {
        this.clipboardNoticeText = "";
        this.clipboardNoticeFadeOut = false;
      }, 2000);
    }

  }

});
